/**
 * The match, as the UI drives it.
 *
 * `simulateInnings` is a generator that computes one whole over per step and
 * pauses whenever it needs an answer. This class turns that into something a
 * screen can watch: it pumps the generator, streams the over's deliveries into
 * the feed on a timer, and stops when a decision is the user's to make.
 *
 * Everything about *cricket* stays in the engine. This file decides only who
 * is asked and how fast the balls appear.
 */

import {
  simulateInnings,
  SUPER_OVER_FORMAT,
  autoAnswer,
  chooseAggression,
  createRng,
  deriveSeed,
  TOSS_FIELD_RATE,
  OVERS_PER_INNINGS,
  BALLS_PER_OVER,
  type Answer,
  type BallEvent,
  type Coefficients,
  type Decision,
  type InningsResult,
  type Player,
  type Yielded,
} from '@ipl-sim/engine-cricket';
import type { Card } from './roster';
import type { Opponent } from './cpu';
import { track } from './analytics';
import { recordMatch } from './record';

export type MatchStage = 'TOSS' | 'PLAYING' | 'INNINGS_BREAK' | 'DONE';

/**
 * A 1v1 match, where this device plays exactly one innings.
 *
 * The toss belongs to the room, not to this object, and the opponent's innings
 * happens on their phone. So there is no toss stage, no innings break, and the
 * result is not decided here -- the server decides it once both innings are in.
 */
export interface SoloInnings {
  /** 0 when batting first, 1 when chasing. */
  readonly inningsIndex: 0 | 1;
  /** Runs needed to win. Present only when chasing. */
  readonly target?: number;
  /** One over, two wickets. */
  readonly superOver?: boolean;
  /**
   * Overrides the match seed. A super over has its own, derived per round, so a
   * repeated one is genuinely different rather than the same six balls again.
   */
  readonly seed?: string;
}

/** What the room needs to accept an innings. */
export interface InningsSubmission {
  readonly decisions: readonly Answer[];
  readonly claimed: { runs: number; wickets: number; legalBalls: number };
  readonly coefficientVersion: number;
}

/** What the UI is being asked for right now, or null when it should just watch. */
export type Prompt =
  | { readonly kind: 'SELECT_BOWLER'; readonly overIndex: number; readonly eligible: readonly Player[] }
  | { readonly kind: 'TIMEOUT'; readonly overIndex: number; readonly forced: boolean };

/** A live batting line. Accumulated from events, so it can never disagree with them. */
export interface LiveBatter {
  readonly player: Player;
  runs: number;
  balls: number;
  out: boolean;
  how?: string;
}

export interface LiveBowler {
  readonly player: Player;
  balls: number;
  runs: number;
  wickets: number;
}

/** Milliseconds between deliveries. */
const BALL_INTERVAL = 750;
/** How many lines the feed keeps. Beyond this the DOM cost is real and nobody scrolls that far. */
const FEED_LIMIT = 60;

export class Match {
  // Placeholders, overwritten in the constructor. Class field initializers run
  // before the constructor body, so the `$derived` fields below would otherwise
  // be reading names a typechecker can prove are not assigned yet. They are
  // lazy, so nothing reads them until well after construction.
  readonly seed: string = undefined!;
  readonly youName: string = undefined!;
  readonly youXI: readonly Card[] = undefined!;
  readonly opponent: Opponent = undefined!;

  stage = $state<MatchStage>('TOSS');

  /** Toss. Decided up front so the screen can show it before the user chooses. */
  readonly youWonToss: boolean;
  /** What the CPU elects when it wins the toss. Real captains field 66% of the time. */
  readonly cpuElected: 'BAT' | 'FIELD';
  youBatFirst = $state<boolean | null>(null);

  inningsIndex = $state<0 | 1>(0);
  runs = $state(0);
  wickets = $state(0);
  legalBalls = $state(0);
  target = $state<number | null>(null);

  /**
   * Newest first, which is how a live feed reads.
   *
   * Each carries a sequence number because nothing else about a delivery is
   * unique — two dot balls in the same over from the same bowler at the same
   * score are identical, and keying a list on them makes Svelte throw.
   */
  feed = $state<{ seq: number; event: BallEvent }[]>([]);
  #seq = 0;
  batting = $state<LiveBatter[]>([]);
  bowling = $state<LiveBowler[]>([]);

  prompt = $state<Prompt | null>(null);
  firstInnings = $state<InningsResult | null>(null);
  secondInnings = $state<InningsResult | null>(null);

  #gen: Generator<Yielded, InningsResult, Answer> | null = null;
  #cpuOvers = new Map<string, number>();
  #timer: ReturnType<typeof setTimeout> | undefined;
  #queue: BallEvent[] = [];
  /** What to do once the queue has been played out. See #drain. */
  #pendingThen: (() => void) | null = null;
  #aggression = 0;
  #stopped = false;
  /**
   * Every answer handed to the generator, in order.
   *
   * This is the decision log the server replays to verify the result, so it
   * must record CPU answers as well as the user's: the replay does not care who
   * chose, only that the same choices produce the same innings.
   */
  #log: Answer[] = [];
  readonly solo: SoloInnings | null = null;
  readonly #startedAt = Date.now();

  /**
   * Passed in, never imported. The fitted table is server-held and fetched at
   * match start -- see lib/coefficients.ts. A Match cannot be constructed
   * without one, which is what stops a default quietly creeping back in.
   */
  readonly coefficients: Coefficients = undefined!;

  constructor(
    youName: string,
    youXI: readonly Card[],
    opponent: Opponent,
    seed: string,
    coefficients: Coefficients,
    solo: SoloInnings | null = null,
  ) {
    this.seed = seed;
    this.youName = youName;
    this.youXI = youXI;
    this.opponent = opponent;
    this.coefficients = coefficients;
    this.solo = solo;

    const rng = createRng(deriveSeed(seed, 'toss'));
    this.youWonToss = rng.chance(0.5);
    this.cpuElected = rng.chance(TOSS_FIELD_RATE) ? 'FIELD' : 'BAT';

    if (solo) {
      // The room already held the toss. Batting first means innings 0.
      this.youBatFirst = solo.inningsIndex === 0;
      this.target = solo.target ?? null;
      this.stage = 'PLAYING';
      this.#startInnings(solo.inningsIndex);
    }
  }

  // ── derived views ──────────────────────────────────────────────────────

  readonly overs = $derived(`${Math.floor(this.legalBalls / 6)}.${this.legalBalls % 6}`);

  /** True when the user's side is the one batting in the current innings. */
  readonly youAreBatting = $derived(
    this.youBatFirst === null ? false : this.inningsIndex === 0 ? this.youBatFirst : !this.youBatFirst,
  );

  readonly chase = $derived(
    this.target === null
      ? null
      : {
          runsRequired: Math.max(0, this.target - this.runs),
          ballsRemaining: OVERS_PER_INNINGS * BALLS_PER_OVER - this.legalBalls,
        },
  );

  /** How many overs this innings runs to. A super over is one. */
  readonly overs_total = $derived(this.solo?.superOver ? 1 : OVERS_PER_INNINGS);

  /** One cell per over, for the progress strip in the score bar. */
  readonly overProgress = $derived(
    Array.from({ length: this.overs_total }, (_, i) => i < Math.floor(this.legalBalls / 6)),
  );

  readonly battingSideName = $derived(this.youAreBatting ? this.youName : this.opponent.name);
  readonly bowlingSideName = $derived(this.youAreBatting ? this.opponent.name : this.youName);

  // ── toss ───────────────────────────────────────────────────────────────

  /**
   * The user's toss call, or the CPU's if it won. Starts the first innings.
   */
  decideToss(youBat: boolean): void {
    this.youBatFirst = this.youWonToss ? youBat : this.cpuElected === 'FIELD';
    track('toss_result', {
      wonToss: this.youWonToss,
      elected: this.youBatFirst ? 'BAT' : 'FIELD',
    });
    track('match_start', { mode: 'cpu', opponentType: 'cpu' });
    this.stage = 'PLAYING';
    this.#startInnings(0);
  }

  // ── innings plumbing ───────────────────────────────────────────────────

  #startInnings(index: 0 | 1): void {
    // In a solo innings this device is always the batting side -- the opponent
    // is not here, so their bowling is answered by the CPU and recorded in the
    // log like any other decision.
    const youBat = this.solo ? true : index === 0 ? this.youBatFirst! : !this.youBatFirst!;
    const battingXI = youBat ? this.youXI : this.opponent.xi;
    const bowlingXI = youBat ? this.opponent.xi : this.youXI;

    this.inningsIndex = index;
    this.runs = 0;
    this.wickets = 0;
    this.legalBalls = 0;
    this.feed = [];
    this.#queue = [];
    this.#cpuOvers = new Map();
    this.#aggression = 0;

    this.batting = battingXI.map((player) => ({ player, runs: 0, balls: 0, out: false }));
    this.bowling = bowlingXI.map((player) => ({ player, balls: 0, runs: 0, wickets: 0 }));

    this.#gen = simulateInnings({
      seed: this.solo?.seed ?? this.seed,
      inningsIndex: index,
      battingXI: battingXI as readonly Player[],
      bowlingXI: bowlingXI as readonly Player[],
      coefficients: this.coefficients,
      target: index === 1 ? this.target! : undefined,
      format: this.solo?.superOver ? SUPER_OVER_FORMAT : undefined,
    });

    this.#pump();
  }

  /**
   * Advances the generator until it either finishes or asks something the user
   * must answer. Deliveries handed back on the way are queued for the feed.
   */
  #pump(answer?: Answer): void {
    const gen = this.#gen;
    if (!gen || this.#stopped) return;

    if (answer !== undefined) this.#log.push(answer);
    let step = answer === undefined ? gen.next() : gen.next(answer);

    let guard = 0;
    while (!step.done) {
      if (++guard > 500) throw new Error('match decision loop is stuck');

      this.#queue.push(...step.value.events);

      const decision = step.value.decision;
      if (this.#isUsers(decision)) {
        // Show the queued deliveries first, then ask. Asking before the over
        // has been seen would make the user choose a bowler for an over whose
        // result they have not watched yet.
        this.#drain(() => this.#ask(decision));
        return;
      }

      const cpu = this.#cpuAnswer(decision);
      this.#log.push(cpu);
      step = gen.next(cpu);
    }

    // Innings over. Its final deliveries never arrive through a yield, because
    // nothing more is asked -- take them from the result.
    const result = step.value;
    const seen = this.feed.length + this.#queue.length;
    this.#queue.push(...result.events.slice(seen));
    this.#gen = null;
    this.#drain(() => this.#endInnings(result));
  }

  #isUsers(decision: Decision): boolean {
    if (decision.type === 'SELECT_BOWLER') return !this.youAreBatting;
    // Only the batting side's break changes anything -- the engine applies an
    // aggression answer only when `side === 'batting'`. Prompting a bowling
    // captain would be a slider that does nothing.
    return decision.side === 'batting' && this.youAreBatting;
  }

  #cpuAnswer(decision: Decision): Answer {
    // Whatever the most recent delivery is: the tail of what is still queued,
    // or the head of the feed once the queue has drained.
    const last: BallEvent | undefined = this.#queue.at(-1) ?? this.feed[0]?.event;
    const chase =
      decision.type === 'STRATEGIC_TIMEOUT' && this.target !== null
        ? {
            runsRequired: Math.max(0, this.target - (last?.score.runs ?? this.runs)),
            ballsRemaining:
              OVERS_PER_INNINGS * BALLS_PER_OVER - (this.legalBalls + this.#queue.length),
          }
        : last?.chase;
    return autoAnswer(decision, this.#bowlingXI(), this.#cpuOvers, chase);
  }

  #bowlingXI(): readonly Player[] {
    return (this.youAreBatting ? this.opponent.xi : this.youXI) as readonly Player[];
  }

  #ask(decision: Decision): void {
    this.prompt =
      decision.type === 'SELECT_BOWLER'
        ? { kind: 'SELECT_BOWLER', overIndex: decision.overIndex, eligible: decision.eligible }
        : { kind: 'TIMEOUT', overIndex: decision.overIndex, forced: decision.forced };
  }

  // ── the user's answers ─────────────────────────────────────────────────

  selectBowler(playerId: string): void {
    if (this.prompt?.kind !== 'SELECT_BOWLER') return;
    // Check against the list the engine actually offered. A tap that lands
    // while the screen is mid-update would otherwise reach `simulateInnings`,
    // which treats an ineligible bowler as a programming error and throws --
    // killing the match rather than ignoring one stray tap.
    if (!this.prompt.eligible.some((p) => p.id === playerId)) return;
    const bowler = this.prompt.eligible.find((p) => p.id === playerId);
    track('bowler_selected', {
      over: this.prompt.overIndex + 1,
      bowlerRating: bowler?.bowl ?? 0,
    });
    this.prompt = null;
    this.#pump({ type: 'BOWLER', playerId });
  }

  setAggression(value: number): void {
    if (this.prompt?.kind !== 'TIMEOUT') return;
    track('timeout_called', {
      side: 'batting',
      over: this.prompt.overIndex + 1,
      forced: this.prompt.forced,
      aggression: value,
    });
    this.prompt = null;
    this.#aggression = value;
    this.#pump({ type: 'AGGRESSION', value });
  }

  declineTimeout(): void {
    if (this.prompt?.kind !== 'TIMEOUT') return;
    const forced = this.prompt.forced;
    this.prompt = null;
    // A forced break cannot be skipped, only left at the current setting.
    this.#pump(forced ? { type: 'AGGRESSION', value: this.#aggression } : { type: 'DECLINE' });
  }

  /**
   * What to send the room. Available once a solo innings is done.
   *
   * The claim is deliberately just the three totals: everything else is
   * re-derived server-side from the decision log, so sending a full scorecard
   * would be sending numbers nobody trusts.
   */
  submission(): InningsSubmission | null {
    if (!this.solo || this.stage !== 'DONE') return null;
    const result = this.solo.inningsIndex === 0 ? this.firstInnings : this.secondInnings;
    if (!result) return null;
    return {
      decisions: [...this.#log],
      claimed: { runs: result.runs, wickets: result.wickets, legalBalls: result.legalBalls },
      coefficientVersion: this.coefficients.version,
    };
  }

  /** The aggression the CPU would pick here, offered as the slider's default. */
  suggestedAggression(): number {
    const c = this.chase;
    return chooseAggression(c !== null, c?.runsRequired ?? 0, c?.ballsRemaining ?? 0);
  }

  // ── the feed ───────────────────────────────────────────────────────────

  /**
   * Plays the queued deliveries out one at a time, then runs `then`.
   *
   * The continuation is held on the instance rather than captured per timeout,
   * so that skipping ahead can still run it. Cancelling the timer without it
   * left the match frozen: every delivery appeared, and then nothing ever asked
   * for the next over.
   */
  #drain(then: () => void): void {
    clearTimeout(this.#timer);
    this.#pendingThen = then;
    this.#step();
  }

  #step(): void {
    if (this.#stopped) return;

    if (this.#queue.length === 0) {
      const then = this.#pendingThen;
      this.#pendingThen = null;
      then?.();
      return;
    }

    this.#timer = setTimeout(() => {
      const event = this.#queue.shift();
      if (event) this.#apply(event);
      this.#step();
    }, BALL_INTERVAL);
  }

  /** Skips the animation and shows everything queued at once. */
  fastForward(): void {
    clearTimeout(this.#timer);
    while (this.#queue.length) this.#apply(this.#queue.shift()!);
    this.#step();
  }

  #apply(e: BallEvent): void {
    this.runs = e.score.runs;
    this.wickets = e.score.wickets;
    if (e.isLegal) this.legalBalls++;

    const batter = this.batting.find((b) => b.player.id === e.striker.id);
    if (batter) {
      batter.runs = e.strikerCard.runs;
      batter.balls = e.strikerCard.balls;
      if (e.wicket && e.wicket.batter.id === e.striker.id) {
        batter.out = true;
        batter.how = this.#dismissalText(e);
      }
    }

    const bowler = this.bowling.find((b) => b.player.id === e.bowler.id);
    if (bowler) {
      bowler.balls = e.bowlerFigures.balls;
      bowler.runs = e.bowlerFigures.runs;
      bowler.wickets = e.bowlerFigures.wickets;
    }

    this.feed = [{ seq: this.#seq++, event: e }, ...this.feed].slice(0, FEED_LIMIT);
  }

  /** Scorecard conventions -- "b Bowler", not "bowled b Bowler". */
  #dismissalText(e: BallEvent): string {
    const w = e.wicket!;
    const by = w.bowler?.name ?? '';
    if (w.kind === 'run out') return 'run out';
    if (w.kind === 'caught' && w.fielder) return `c ${w.fielder.name} b ${by}`.trim();
    if (w.kind === 'caught and bowled') return `c & b ${by}`.trim();
    if (w.kind === 'stumped') return `st b ${by}`.trim();
    if (w.kind === 'bowled') return `b ${by}`.trim();
    if (w.kind === 'lbw') return `lbw b ${by}`.trim();
    if (w.kind === 'hit wicket') return `hit wicket b ${by}`.trim();
    return 'obstructing the field';
  }

  // ── innings and match end ──────────────────────────────────────────────

  #endInnings(result: InningsResult): void {
    if (this.solo) {
      // One innings, then stop. There is no second innings on this device and
      // no winner to compute -- the room does that once both are submitted.
      if (this.solo.inningsIndex === 0) this.firstInnings = result;
      else this.secondInnings = result;
      this.stage = 'DONE';
      return;
    }

    if (this.inningsIndex === 0) {
      this.firstInnings = result;
      this.target = result.runs + 1;
      this.stage = 'INNINGS_BREAK';
      return;
    }
    this.secondInnings = result;
    this.stage = 'DONE';

    const r = this.result;
    if (r) {
      recordMatch({
        won: r.youWon,
        tied: r.tied,
        scoreFor: this.youBatFirst ? r.first.runs : r.second.runs,
      });
      track('match_completed', {
        result: r.tied ? 'tie' : r.youWon ? 'win' : 'loss',
        margin: r.margin,
        scoreFor: this.youBatFirst ? r.first.runs : r.second.runs,
        scoreAgainst: this.youBatFirst ? r.second.runs : r.first.runs,
        durationMs: Date.now() - this.#startedAt,
        mode: 'cpu',
      });
    }
  }

  startSecondInnings(): void {
    if (this.stage !== 'INNINGS_BREAK') return;
    this.stage = 'PLAYING';
    this.#startInnings(1);
  }

  /**
   * Stops all timers. Must be called when the screen goes away.
   *
   * Also the only moment we learn a match was abandoned: leaving mid-innings is
   * indistinguishable from any other teardown, so it is recorded here rather
   * than at some notional "quit" button that does not exist.
   */
  dispose(): void {
    if (!this.#stopped && this.stage === 'PLAYING') {
      track('match_abandoned', { over: Math.floor(this.legalBalls / 6) + 1, mode: 'cpu' });
    }
    this.#stopped = true;
    clearTimeout(this.#timer);
  }

  // ── result ─────────────────────────────────────────────────────────────

  readonly result = $derived.by(() => {
    const a = this.firstInnings;
    const b = this.secondInnings;
    if (!a || !b) return null;

    const youBattedFirst = this.youBatFirst === true;
    const tied = b.runs === a.runs;
    const chaseWon = b.runs > a.runs;
    const youWon = tied ? false : chaseWon !== youBattedFirst;

    // Cricket states a win by wickets when chasing and by runs when defending;
    // saying "won by 54 runs" about a successful chase would be wrong, not just
    // unidiomatic.
    let margin: string;
    if (tied) margin = 'Match tied';
    else if (chaseWon) margin = `Won by ${10 - b.wickets} wicket${10 - b.wickets === 1 ? '' : 's'}`;
    else margin = `Won by ${a.runs - b.runs} run${a.runs - b.runs === 1 ? '' : 's'}`;

    const ballsLeft = OVERS_PER_INNINGS * BALLS_PER_OVER - b.legalBalls;
    return {
      tied,
      youWon,
      chaseWon,
      headline: tied ? 'Match tied' : youWon ? margin : margin.replace('Won', 'Lost'),
      margin,
      detail: chaseWon && !tied && ballsLeft > 0 ? `${ballsLeft} ball${ballsLeft === 1 ? '' : 's'} to spare` : '',
      first: a,
      second: b,
      firstName: youBattedFirst ? this.youName : this.opponent.name,
      secondName: youBattedFirst ? this.opponent.name : this.youName,
    };
  });
}
